import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { CookieConsentProvider } from './contexts/CookieConsentContext';
import { Toaster as UIToaster } from './components/ui/toaster';
import { TooltipProvider } from './components/ui/tooltip';
import { queryClient } from './lib/queryClient';
import ErrorBoundary from './components/ui/error-boundary';
import RoleGuard from './components/auth/RoleGuard';
import CookieConsent from './components/CookieConsent';
import AnalyticsTracker from './components/AnalyticsTracker';
import LoadingSpinner from './components/ui/loading-spinner';

import LandingPage from './pages/LandingPage';

// FE-006: these six were eagerly imported alongside LandingPage, so they shipped in every
// visitor's entry chunk even though none of them is the default route -- unlike every auth/
// dashboard page below, which is correctly lazy. LandingPage stays eager since it's the actual
// "/" route.
const ContactPage = lazy(() => import('./pages/ContactPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const OAuthCallbackPage = lazy(() => import('./pages/OAuthCallbackPage'));

const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const SetupPasswordPage = lazy(() => import('./pages/auth/SetupPasswordPage'));
const AcceptInvitationPage = lazy(() => import('./pages/auth/AcceptInvitationPage'));

const DashboardLayout = lazy(() => import('./components/layout/DashboardLayout'));
const RoleBasedDashboard = lazy(() => import('./components/dashboard/RoleBasedDashboard'));

const BusinessPage = lazy(() => import('./pages/dashboard/BusinessPage'));
const BusinessDetailPage = lazy(() => import('./pages/dashboard/BusinessDetailPage'));
const ReviewPage = lazy(() => import('./pages/dashboard/ReviewPage'));
const ReviewDetailPage = lazy(() => import('./pages/dashboard/ReviewDetailPage'));
const OrganizationPage = lazy(() => import('./pages/dashboard/OrganizationPage'));
const SubscriptionPage = lazy(() => import('./pages/dashboard/SubscriptionPage'));
const SettingsPage = lazy(() => import('./pages/dashboard/SettingsPage'));
const ApiKeysPage = lazy(() => import('./pages/dashboard/ApiKeysPage'));
const AgencyDashboardPage = lazy(() => import('./pages/dashboard/AgencyDashboardPage'));

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const [searchParams] = useSearchParams();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated) {
    // Honors ?returnUrl= (e.g. /login?returnUrl=/invitations/accept?token=...)
    // so a user who had to log in mid-flow lands back where they were, not
    // always /dashboard. This redirect fires reactively the instant
    // isAuthenticated flips true -- it's the single source of truth for
    // post-login navigation here; LoginPage itself must NOT also call
    // navigate() on login success, or the two would race (this one always
    // wins, since it re-renders synchronously with the auth-state change,
    // which silently made ?returnUrl= a no-op before this fix). Only a
    // same-origin relative path starting with a single "/" is honored --
    // this value is attacker-controllable via the URL.
    const returnUrl = searchParams.get('returnUrl');
    const safeReturnUrl = returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('//') ? returnUrl : null;
    return <Navigate to={safeReturnUrl ?? '/dashboard'} replace />;
  }

  return <>{children}</>;
};

const RouteErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ErrorBoundary>{children}</ErrorBoundary>
);

const LazyPage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <RouteErrorBoundary>
    <Suspense fallback={<LoadingSpinner />}>{children}</Suspense>
  </RouteErrorBoundary>
);

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <LanguageProvider>
              <CookieConsentProvider>
                <AuthProvider>
                  <TooltipProvider delayDuration={200}>
                  <Router
                    future={{
                      v7_startTransition: true,
                      v7_relativeSplatPath: true,
                    }}
                  >
                    <div className="App">
                      <AnalyticsTracker />
                      <Routes>
                        <Route path="/" element={<LandingPage />} />
                        <Route
                          path="/about"
                          element={
                            <LazyPage>
                              <AboutPage />
                            </LazyPage>
                          }
                        />
                        <Route
                          path="/contact"
                          element={
                            <LazyPage>
                              <ContactPage />
                            </LazyPage>
                          }
                        />
                        <Route
                          path="/privacy"
                          element={
                            <LazyPage>
                              <PrivacyPolicyPage />
                            </LazyPage>
                          }
                        />
                        <Route
                          path="/cookies"
                          element={
                            <LazyPage>
                              <CookiePolicyPage />
                            </LazyPage>
                          }
                        />
                        <Route
                          path="/terms"
                          element={
                            <LazyPage>
                              <TermsOfServicePage />
                            </LazyPage>
                          }
                        />
                        <Route
                          path="/oauth/callback"
                          element={
                            <LazyPage>
                              <OAuthCallbackPage />
                            </LazyPage>
                          }
                        />
                        <Route
                          path="/invitations/accept"
                          element={
                            <LazyPage>
                              <AcceptInvitationPage />
                            </LazyPage>
                          }
                        />

                        <Route
                          path="/login"
                          element={
                            <PublicRoute>
                              <LazyPage>
                                <LoginPage />
                              </LazyPage>
                            </PublicRoute>
                          }
                        />
                        <Route
                          path="/register"
                          element={
                            <PublicRoute>
                              <LazyPage>
                                <RegisterPage />
                              </LazyPage>
                            </PublicRoute>
                          }
                        />
                        <Route
                          path="/forgot-password"
                          element={
                            <PublicRoute>
                              <LazyPage>
                                <ForgotPasswordPage />
                              </LazyPage>
                            </PublicRoute>
                          }
                        />
                        <Route
                          path="/setup-password"
                          element={
                            <PublicRoute>
                              <LazyPage>
                                <SetupPasswordPage />
                              </LazyPage>
                            </PublicRoute>
                          }
                        />

                        <Route
                          path="/dashboard/*"
                          element={
                            <ProtectedRoute>
                              <LazyPage>
                                <DashboardLayout />
                              </LazyPage>
                            </ProtectedRoute>
                          }
                        >
                          <Route
                            index
                            element={
                              <LazyPage>
                                <RoleBasedDashboard />
                              </LazyPage>
                            }
                          />
                          <Route
                            path="businesses"
                            element={
                              <LazyPage>
                                <BusinessPage />
                              </LazyPage>
                            }
                          />
                          <Route
                            path="businesses/:businessId"
                            element={
                              <LazyPage>
                                <BusinessDetailPage />
                              </LazyPage>
                            }
                          />
                          <Route
                            path="reviews"
                            element={
                              <LazyPage>
                                <ReviewPage />
                              </LazyPage>
                            }
                          />
                          <Route
                            path="reviews/:reviewId"
                            element={
                              <LazyPage>
                                <ReviewDetailPage />
                              </LazyPage>
                            }
                          />
                          <Route
                            path="organizations"
                            element={
                              <RoleGuard allowedRoles={['SystemAdmin']}>
                                <LazyPage>
                                  <OrganizationPage />
                                </LazyPage>
                              </RoleGuard>
                            }
                          />
                          <Route
                            path="subscription"
                            element={
                              <RoleGuard allowedRoles={['SystemAdmin', 'OrganizationAdmin']}>
                                <LazyPage>
                                  <SubscriptionPage />
                                </LazyPage>
                              </RoleGuard>
                            }
                          />
                          <Route
                            path="settings"
                            element={
                              <LazyPage>
                                <SettingsPage />
                              </LazyPage>
                            }
                          />
                          <Route
                            path="api-keys"
                            element={
                              <RoleGuard allowedRoles={['SystemAdmin', 'OrganizationAdmin']}>
                                <LazyPage>
                                  <ApiKeysPage />
                                </LazyPage>
                              </RoleGuard>
                            }
                          />
                          <Route
                            path="agency"
                            element={
                              <RoleGuard allowedRoles={['SystemAdmin', 'OrganizationAdmin']}>
                                <LazyPage>
                                  <AgencyDashboardPage />
                                </LazyPage>
                              </RoleGuard>
                            }
                          />
                          <Route path="profile" element={<Navigate to="/dashboard/settings" replace />} />
                        </Route>

                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>

                      <UIToaster />
                      <CookieConsent />
                    </div>
                  </Router>
                  </TooltipProvider>
                  <ReactQueryDevtools initialIsOpen={false} />
                </AuthProvider>
              </CookieConsentProvider>
            </LanguageProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
